// EL ESTADO REAL DE UNA TERMINAL DE PRODUCTO EN SU TARJETA · DISEÑO-F5 §7.1
//
// Lo que se pinta aquí viene YA DECIDIDO del servidor (`estado_v2`,
// `callada_desde`, `ram_baja`…): la pantalla no calcula nada, porque la
// regla de «callada» depende del horario y la zona de la empresa, que solo
// el servidor tiene. Los cuatro colores son los de la casa: verde bien,
// amarillo atención, rojo problema, gris sin dato.
//
// UNA TARJETA SIN `serie` (terminal de conteo v1) no pasa por aquí: todos
// estos campos llegan nulos y la tarjeta se ve exactamente como siempre.
import { useState } from 'react';
import { Fingerprint, Wifi, WifiOff, AlertTriangle, MemoryStick } from 'lucide-react';
import { api } from '../api';
import Ayuda from '../ui/Ayuda';
import Motivo from '../experiencia/Motivo';
import { leerErrorDelContrato } from './errorDelContrato';
import { esDeProducto, hora, kb, SENAL_EN_PALABRAS, COLOR_DE_ESTADO } from './estadoDeTerminalUtil';

function minutosDesde(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  return Number.isFinite(ms) ? Math.max(0, Math.round(ms / 60000)) : null;
}

const CLASES = {
  verde: 'text-green-800', ambar: 'text-amber-800', rojo: 'text-red-800', gris: 'text-gray-600',
};

/** Las dos líneas de estado de una terminal de producto (sin la retirada,
 *  que la tarjeta ya dice). */
export function LineaDeEstado({ t }) {
  if (!esDeProducto(t) || !t.estado_v2 || t.estado_v2 === 'RETIRADA') return null;
  const color = COLOR_DE_ESTADO[t.estado_v2] || 'gris';
  let principal;
  let consejo = null;
  if (t.estado_v2 === 'EN_LINEA') {
    principal = <><Wifi size={13} className="shrink-0" /> Dio señales hace un momento</>;
  } else if (t.estado_v2 === 'CALLADA') {
    const n = minutosDesde(t.sin_red_desde);
    principal = (
      <>
        <AlertTriangle size={13} className="shrink-0" />
        {' '}Callada desde las {hora(t.callada_desde)}: no da señales desde hace {n ?? '—'} min y está en horario de trabajo.
        <Ayuda id="terminal.estado.callada" />
      </>
    );
    consejo = 'Revisa que tenga corriente y Wi-Fi. Lo que capturó sin red se enviará solo cuando vuelva.';
  } else if (t.estado_v2 === 'SIN_RED') {
    principal = (
      <>
        <WifiOff size={13} className="shrink-0" />
        {' '}{t.sin_red_desde
          ? `Sin señales desde las ${hora(t.sin_red_desde)} (fuera del horario de trabajo).`
          : 'Todavía no se ha conectado ni una vez.'}
      </>
    );
  } else if (t.estado_v2 === 'SIN_VINCULAR') {
    principal = <><WifiOff size={13} className="shrink-0" /> Enciéndela: su pantalla te dará un código</>;
  } else {
    principal = <><AlertTriangle size={13} className="shrink-0" /> Robada: su serie está bloqueada</>;
  }
  const detalles = [];
  if (t.en_cola !== null && t.en_cola !== undefined) detalles.push(`En cola: ${t.en_cola}`);
  if (t.senal) detalles.push(`Señal ${SENAL_EN_PALABRAS[t.senal] || t.senal.toLowerCase()} (${t.senal_dbm})`);
  if (t.firmware) detalles.push(`firmware ${t.firmware}`);
  if (t.contrato) detalles.push(`contrato ${t.contrato}`);
  return (
    <div className="text-sm space-y-0.5" data-terminal="estado" data-estado={t.estado_v2}>
      <div className={`flex items-start gap-1 ${CLASES[color]}`}>{principal}</div>
      {consejo && <div className="text-gray-700">{consejo}</div>}
      {detalles.length > 0 && <div className="text-gray-600">{detalles.join(' · ')}</div>}
      <LineaDeMemoria t={t} />
      <LineaDeTurno t={t} />
    </div>
  );
}

export function LineaDeMemoria({ t }) {
  if (!esDeProducto(t)) return null;
  const minimo = kb(t.ram_minima);
  if (minimo === null) {
    return (
      <div className="text-gray-500 flex items-center gap-1" data-terminal="memoria" data-tono="gris">
        <MemoryStick size={13} className="shrink-0" /> Memoria: sin medir todavía <Ayuda id="terminal.estado.ram" />
      </div>
    );
  }
  if (t.ram_baja) {
    const n = t.reinicios_por_memoria || 0;
    return (
      <div className="text-amber-800" data-terminal="memoria" data-tono="ambar">
        <div className="flex items-center gap-1">
          <MemoryStick size={13} className="shrink-0" />
          {' '}Memoria baja: {minimo}.{n > 0 ? ` Se reinició ${n} ${n === 1 ? 'vez' : 'veces'} para no perder nada.` : ''}
          <Ayuda id="terminal.estado.ram" />
        </div>
        <div className="text-gray-700">Si se repite varios días, avisa a TEGO con el número de serie.</div>
      </div>
    );
  }
  return (
    <div className="text-green-800 flex items-center gap-1" data-terminal="memoria" data-tono="verde">
      <MemoryStick size={13} className="shrink-0" /> Memoria libre mínima: {minimo} <Ayuda id="terminal.estado.ram" />
    </div>
  );
}

export function LineaDeTurno({ t }) {
  if (!esDeProducto(t) || t.estado_v2 === 'SIN_VINCULAR') return null;
  if (t.turno?.operador) {
    return <div className="text-gray-700">{t.turno.operador}, desde las {hora(t.turno.desde)}</div>;
  }
  return <div className="text-gray-500">Nadie en turno</div>;
}

/** El botón «Identificar»: la terminal parpadea y dice su nombre. Apagado
 *  con motivo si no está vinculada o está robada (regla de la fachada). */
export function Identificar({ t, puede = true, onHecho, onError, onRecargar }) {
  const [enviando, setEnviando] = useState(false);
  if (!esDeProducto(t) || !t.activo) return null;
  let motivo = null;
  if (!puede) motivo = 'Tu puesto no puede pedirle nada a la terminal; pídeselo a un encargado.';
  else if (t.robada_en) motivo = 'Está marcada como robada: no recibe órdenes. Si apareció, pulsa «Apareció».';
  else if (t.estado_v2 === 'SIN_VINCULAR' || !t.vinculada_en) motivo = 'No está vinculada: enciéndela y su pantalla te dará un código.';
  const id = `motivo-identificar-${t.id}`;

  async function pedir() {
    setEnviando(true);
    try {
      const d = await api.post(`/terminales/${t.id}/identificar`, {});
      onHecho?.({ mensaje: d?.dice || 'Parpadeará y dirá su nombre en menos de un minuto.' });
      if (onRecargar) setTimeout(onRecargar, 60000);
    } catch (e) {
      const { mensaje, queHacer } = leerErrorDelContrato(e);
      onError?.(`${mensaje} ${queHacer}`.trim());
    } finally {
      setEnviando(false);
    }
  }

  return (
    <span className="inline-flex flex-col gap-1">
      <span className="inline-flex items-center gap-1">
        <button
          type="button"
          onClick={pedir}
          disabled={!!motivo || enviando}
          aria-describedby={motivo ? id : undefined}
          className="inline-flex min-h-11 items-center gap-1.5 border border-gray-300 bg-white rounded-lg px-3 py-1.5 text-sm font-semibold text-gray-700 disabled:opacity-50"
        >
          <Fingerprint size={15} aria-hidden="true" />
          {enviando ? 'Un momento…' : 'Identificar'}
        </button>
        <Ayuda id="terminal.identificar.boton" />
      </span>
      <Motivo id={id} cuando={!!motivo}>{motivo}</Motivo>
    </span>
  );
}

/** Los chips de arriba: «Todas» y «Con problema (n)». Se desplazan solos a
 *  320 px (no la página). */
export function ChipsDeFiltro({ valor, onCambio, conProblema }) {
  const chips = [['TODAS', 'Todas'], ['PROBLEMA', `Con problema (${conProblema})`]];
  return (
    <div role="group" aria-label="Filtrar terminales" className="flex gap-2 overflow-x-auto max-w-full pb-1" data-terminal="chips">
      {chips.map(([clave, rotulo]) => (
        <button
          key={clave}
          type="button"
          onClick={() => onCambio(clave)}
          aria-pressed={valor === clave}
          className={`min-h-11 whitespace-nowrap rounded-full border px-3 text-sm font-semibold ${
            valor === clave ? 'border-[var(--color-marca)] text-[var(--color-marca)]' : 'border-gray-300 text-gray-700'}`}
        >
          {rotulo}
        </button>
      ))}
    </div>
  );
}
